import { webConfig } from '@gitbook/env';
import { Trace } from '@gitbook/util-debug';
import { extname } from '@gitbook/util-files';

import {
    getBucketNameFromDownloadURL,
    getBucketNameFromLocation,
    parseStorageDownloadURL,
} from './buckets';
import { duplicateFileInBucket, getNativeFileMetadata, uploadNativeFile } from './implementation';
import { getFilePath } from './paths';
import type { FileLocation, FileUploadMetadata, NativeFile } from './types';
import { isImageMimeType as isImageMimeTypeUtil } from './utils';

/**
 * Upload a file in a workspace.
 */
export async function uploadFile(
    location: FileLocation,
    file: NativeFile
): Promise<FileUploadMetadata> {
    const trace = new Trace('storage.upload');

    return trace.wrap(() => uploadNativeFile(location, file));
}

/**
 * Get the file metadata for a given download URL or a location.
 * It returns null if the file doesn't exist.
 */
export async function getFileMetadata(
    input: string | { location: FileLocation; name: string }
): Promise<FileUploadMetadata | null> {
    const trace = new Trace('storage.getFileMetadata');

    const location =
        typeof input === 'string'
            ? parseStorageDownloadURL(input)
            : {
                  bucket: getBucketNameFromLocation(input.location),
                  path: getFilePath(input.location, input.name),
              };

    const metadata = await trace.wrap(() =>
        getNativeFileMetadata(
            location.bucket,
            location.path,
            typeof input === 'string' ? undefined : input.location.kind
        )
    );
    return metadata;
}

/**
 * Upload multiple files in a workplace.
 */
export async function uploadFiles(
    location: FileLocation,
    files: NativeFile[]
): Promise<FileUploadMetadata[]> {
    const results: FileUploadMetadata[] = [];

    await Promise.all(
        files.map((file, index) =>
            uploadFile(location, file).then((result) => {
                results[index] = result;
            })
        )
    );

    return results;
}

/**
 * Returns true if the given mime type is considered an image.
 */
export function isImageMimeType(mimeType: string) {
    return isImageMimeTypeUtil(mimeType);
}

/**
 * Returns true if the given file is considered an image.
 */
export function isImageFile(file: NativeFile | FileUploadMetadata) {
    const mimeType = 'contentType' in file ? file.contentType : file.type;
    return isImageMimeTypeUtil(mimeType);
}

/**
 * Returns true if the given file is a png or jpg image.
 */
export function isPngOrJpgFile(file: NativeFile | FileUploadMetadata) {
    const mimeType = 'contentType' in file ? file.contentType : file.type;
    return mimeType === 'image/png' || mimeType === 'image/jpeg';
}

/**
 * Returns true if the given file is considered a drawing.
 */
export function isDrawingFile(file: NativeFile | FileUploadMetadata) {
    const name = file.name.toLowerCase();
    return name.endsWith('.drawing.svg') || name.endsWith('.excalidraw.svg');
}

/**
 * Returns true if the given file is considered an openapi/swagger file.
 */
export function isOpenAPIFile(file: NativeFile | FileUploadMetadata) {
    const mimeType = 'contentType' in file ? file.contentType : file.type;

    if (
        mimeType === 'application/json' ||
        mimeType === 'application/yaml' ||
        mimeType === 'text/yaml' ||
        mimeType === 'application/x-yaml' ||
        mimeType === 'text/x-yaml'
    ) {
        return true;
    }

    // Fallback to extension as mime-type might not be available fot YAML/JSON
    const extension = extname(file.name);

    return extension === '.json' || extension === '.yaml' || extension === '.yml';
}

/**
 * Duplicate a file already in a bucket.
 */
export async function duplicateFile(
    downloadURL: string,
    toLocation: FileLocation
): Promise<FileUploadMetadata> {
    const trace = new Trace('storage.duplicateFile');

    return trace.wrap(() => duplicateFileInBucket(downloadURL, toLocation));
}

/**
 * Test if an download URL belongs to one of our buckets.
 */
export function isFileInGitBookBucket(downloadURL: string): boolean {
    try {
        const bucket = getBucketNameFromDownloadURL(downloadURL);
        return [webConfig.firebase.storageBucket].includes(bucket);
    } catch (_err) {
        return false;
    }
}
